// File: src/pages/admin/CompaniesAdminPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog } from "@headlessui/react";
import { toast } from "react-hot-toast";
import UnitOperatingSettingsFields from "@/components/admin/UnitOperatingSettingsFields";
import { unitSettingsForm, unitSettingsPatch } from "@/lib/unitOperatingSettings";
import { unitSettingsApi } from "@/lib/unitSettingsApi";
import CompanyCurrencyFields from "@/components/admin/CompanyCurrencyFields";
import CompanyCurrencyEditor from "@/components/admin/CompanyCurrencyEditor";
import { companyCurrencyForm, companyCurrencyPayload } from "@/lib/companySettingsRequests";
import { companySettingsApi } from "@/lib/companySettingsApi";
import { notifyOperationalContextChanged } from "@/lib/procurementOperationalContext";

/**
 * ✅ IMPORTANT:
 * Platform admin must be determined by server truth (claims/flags),
 * NOT by a normal tenant role like "developer".
 */
function isPlatformAdminFromMePayload(data) {
  const u = data?.user ?? data ?? {};
  return (
    Boolean(u?.is_platform_admin) ||
    Boolean(u?.isPlatformAdmin) ||
    Boolean(u?.platform_admin) ||
    Boolean(u?.platformAdmin)
  );
}

function extractApiError(err, fallback = "Request failed") {
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  if (String(msg).toUpperCase() === "PLATFORM_ADMIN_REQUIRED") {
    return "Forbidden: platform admin access required.";
  }
  if (
    String(msg).toLowerCase().includes("invalid or expired token") ||
    err?.response?.status === 401
  ) {
    return "Unauthorized. Please sign in again.";
  }
  return msg;
}

function toSlugSafe(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Try multiple API paths (helps when backend mount base differs across envs).
 * Only retries on 404; otherwise throws.
 */
async function getFirstWorking(getter) {
  let lastErr = null;
  for (const attempt of getter) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await attempt();
    } catch (e) {
      lastErr = e;
      if (e?.response?.status === 404) continue;
      throw e;
    }
  }
  throw lastErr;
}

/**
 * Debounce hook (simple, dependency-safe).
 */
function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function CompaniesAdminPage() {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  // ✅ Guard: only platform admins should see/use this page
  const [meLoading, setMeLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // ✅ Platform admin context (cookie-backed)
  const [adminCtx, setAdminCtx] = useState(null); // { company_id, unit_id, unit_slug, ... } | null
  const [adminCtxLoading, setAdminCtxLoading] = useState(false);

  // New Company modal state
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [companyWriteUncertain, setCompanyWriteUncertain] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    ...companyCurrencyForm(),
    company_name: "",
    industry: "",
    location: "",
    subscription_plan: "",
  });

  // Seed Admin modal state
  const [seedForCompany, setSeedForCompany] = useState(null);
  const [seedForm, setSeedForm] = useState({
    full_name: "",
    email: "",
    password: "",
  });
  const [seeding, setSeeding] = useState(false);

  // ✅ Units modal state
  const [unitsForCompany, setUnitsForCompany] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState("");
  const [units, setUnits] = useState([]);

  const [unitCreate, setUnitCreate] = useState({
    ...unitSettingsForm(),
    unit_name: "",
    slug: "",
    address: "",
    contact_email: "",
    is_active: true,
  });

  const [editingUnit, setEditingUnit] = useState(null);
  const [unitEdit, setUnitEdit] = useState({
    ...unitSettingsForm(),
    unit_name: "",
    slug: "",
    address: "",
    contact_email: "",
    is_active: true,
  });
  const [unitSaving, setUnitSaving] = useState(false);
  const [unitWriteUncertain, setUnitWriteUncertain] = useState(false);

  const canUsePage = useMemo(
    () => !meLoading && isPlatformAdmin,
    [meLoading, isPlatformAdmin]
  );

  // Avoid state updates after unmount
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Abort controllers (cleanup on unmount)
  const companiesAbortRef = useRef(null);
  const unitsAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      try {
        companiesAbortRef.current?.abort?.();
      } catch {}
      try {
        unitsAbortRef.current?.abort?.();
      } catch {}
    };
  }, []);

  // ---------------- auth guard ----------------
  useEffect(() => {
    (async () => {
      setMeLoading(true);
      setError("");
      try {
        const { data } = await getFirstWorking([
          () => api.get("/auth/me"),
          () => api.get("/solo-mdsh-api/auth/me"),
        ]);

        const ok = isPlatformAdminFromMePayload(data);
        if (!aliveRef.current) return;

        setIsPlatformAdmin(ok);
        if (!ok) setError("Forbidden: platform admin access required.");
      } catch (err) {
        console.error("Failed to load /auth/me", err);
        if (!aliveRef.current) return;

        setIsPlatformAdmin(false);
        setError(extractApiError(err, "Unauthorized. Please sign in again."));
      } finally {
        if (aliveRef.current) setMeLoading(false);
      }
    })();
  }, []);

  // ---------------- admin context helpers ----------------
  const fetchAdminContext = useCallback(async () => {
    if (!canUsePage) return;
    setAdminCtxLoading(true);
    try {
      const { data } = await getFirstWorking([
        () => api.get("/admin/context"),
        () => api.get("/solo-mdsh-api/admin/context"),
      ]);
      if (!aliveRef.current) return;
      setAdminCtx(data?.context || null);
    } catch (err) {
      console.warn("Failed to load /admin/context", err);
      if (!aliveRef.current) return;
      setAdminCtx(null);
    } finally {
      if (aliveRef.current) setAdminCtxLoading(false);
    }
  }, [canUsePage]);

  useEffect(() => {
    if (!canUsePage) return;
    fetchAdminContext();
  }, [canUsePage, fetchAdminContext]);

  const switchAdminContext = async ({ company_id, unit_id, unit_slug }) => {
    if (!canUsePage) return;

    const t = toast.loading("Switching context…");
    try {
      const payload = {
        company_id,
        ...(unit_id ? { unit_id } : {}),
        ...(unit_slug ? { unit_slug } : {}),
      };

      const { data } = await getFirstWorking([
        () => api.post("/admin/context", payload),
        () => api.post("/solo-mdsh-api/admin/context", payload),
      ]);

      setAdminCtx(data?.context || null);
      toast.success("Context updated", { id: t });
      notifyOperationalContextChanged();
    } catch (err) {
      console.error("Failed to switch admin context", err);
      toast.error(extractApiError(err, "Failed to set admin context"), { id: t });
    }
  };

  const setCompanyOnlyContext = async (company_id) => {
    if (!canUsePage) return;

    try {
      const { data } = await getFirstWorking([
        () => api.post("/admin/context", { company_id }),
        () => api.post("/solo-mdsh-api/admin/context", { company_id }),
      ]);
      setAdminCtx(data?.context || null);
      notifyOperationalContextChanged();
    } catch (err) {
      console.warn("Failed to set company-only admin context", err);
      toast.error(extractApiError(err, "Failed to set company context"));
    }
  };

  const clearAdminContext = async () => {
    if (!canUsePage) return;

    const t = toast.loading("Clearing context…");
    try {
      await getFirstWorking([
        () => api.delete("/admin/context"),
        () => api.delete("/solo-mdsh-api/admin/context"),
      ]);
      setAdminCtx(null);
      toast.success("Context cleared", { id: t });
      notifyOperationalContextChanged();
    } catch (err) {
      console.error("Failed to clear admin context", err);
      toast.error(extractApiError(err, "Failed to clear admin context"), { id: t });
    }
  };

  // ---------------- companies ----------------
  const fetchCompanies = useCallback(async ({ recover = false } = {}) => {
    if (!canUsePage) return;

    try {
      companiesAbortRef.current?.abort?.();
    } catch {}
    const controller = new AbortController();
    companiesAbortRef.current = controller;

    setLoading(true);
    setError("");

    const params = qDebounced ? { q: qDebounced } : undefined;

    try {
      const { data } = await getFirstWorking([
        () => api.get("/admin/companies", { params, signal: controller.signal }),
        () => api.get("/admin/companies/companies", { params, signal: controller.signal }),
        () => api.get("/solo-mdsh-api/admin/companies", { params, signal: controller.signal }),
      ]);

      if (!aliveRef.current || controller.signal.aborted || companiesAbortRef.current !== controller) return;

      const list = Array.isArray(data?.companies)
        ? data.companies
        : Array.isArray(data)
        ? data
        : [];

      setCompanies(list);
      if (recover) {
        setCompanyWriteUncertain(false);setShowCompanyForm(false);
        setCompanyForm({...companyCurrencyForm(),company_name:'',industry:'',location:'',subscription_plan:''});
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;

      if (!aliveRef.current || controller.signal.aborted || companiesAbortRef.current !== controller) return;
      console.error("Failed to load companies", err);
      setError(extractApiError(err, "Failed to load companies"));
      setCompanies([]);
    } finally {
      if (aliveRef.current && companiesAbortRef.current === controller) setLoading(false);
    }
  }, [canUsePage, qDebounced]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const submitCompanyForm = async (e) => {
    e.preventDefault();
    if (!canUsePage || companySaving || companyWriteUncertain) return;

    setError("");
    const name = String(companyForm.company_name || "").trim();
    if (!name) {
      toast.error("Company name is required");
      return;
    }

    let settings;
    try { settings = companyCurrencyPayload(companyForm); }
    catch (err) { toast.error(err.message);return; }
    setCompanySaving(true);
    const t = toast.loading("Creating company…");
    try {
      const payload = {
        ...settings,
        company_name: name,
        industry: String(companyForm.industry || "").trim() || null,
        location: String(companyForm.location || "").trim() || null,
        subscription_plan: String(companyForm.subscription_plan || "").trim() || null,
      };

      await companySettingsApi.create(payload);
      if (!aliveRef.current) return;

      toast.success("Company created", { id: t });

      setShowCompanyForm(false);
      setCompanyForm({
        ...companyCurrencyForm(),
        company_name: "",
        industry: "",
        location: "",
        subscription_plan: "",
      });

      await fetchCompanies();
    } catch (err) {
      if (!aliveRef.current) return;
      if (err?.uncertain) setCompanyWriteUncertain(true);
      console.error("Create company error:", err);
      const msg = extractApiError(err, "Failed to create company");
      setError(msg);
      toast.error(msg, { id: t });
    } finally {
      if (aliveRef.current) setCompanySaving(false);
    }
  };

  // ---------------- Company row click -> go to hub ----------------
  const openCompanyHub = async (company) => {
    if (!company?.id) return;
    await setCompanyOnlyContext(company.id);
    navigate(`/admin/companies/${company.id}`);
  };

  // ---------------- Seed admin ----------------
  const openSeedModal = (company) => {
    if (!canUsePage) return;
    setSeedForCompany(company);
    setSeedForm({ full_name: "", email: "", password: "" });
  };

  const closeSeedModal = () => {
    if (seeding) return;
    setSeedForCompany(null);
    setSeedForm({ full_name: "", email: "", password: "" });
  };

  const submitSeedAdmin = async (e) => {
    e.preventDefault();
    if (!seedForCompany || !canUsePage) return;

    const full_name = String(seedForm.full_name || "").trim();
    const email = String(seedForm.email || "").trim();
    const password = String(seedForm.password || "");

    if (!full_name || !email || !password) {
      toast.error("Full name, email and password are required");
      return;
    }

    setSeeding(true);
    const t = toast.loading("Creating company admin…");
    try {
      const path = `/admin/companies/${seedForCompany.id}/seed-admin`;

      await getFirstWorking([
        () => api.post(path, { full_name, email, password }),
        () =>
          api.post(`/admin/companies/companies/${seedForCompany.id}/seed-admin`, {
            full_name,
            email,
            password,
          }),
        () => api.post(`/solo-mdsh-api${path}`, { full_name, email, password }),
      ]);

      toast.success(`Admin user created for ${seedForCompany.company_name}`, { id: t });
      closeSeedModal();
      await fetchCompanies();
    } catch (err) {
      console.error("Seed admin error:", err);
      toast.error(extractApiError(err, "Failed to create admin"), { id: t });
    } finally {
      setSeeding(false);
    }
  };

  // ---------------- Units handlers ----------------
  const fetchUnits = async (companyId, { recover = false } = {}) => {
    try {
      unitsAbortRef.current?.abort?.();
    } catch {}
    const controller = new AbortController();
    unitsAbortRef.current = controller;

    setUnitsLoading(true);
    setUnitsError("");
    try {
      const { units: rows } = await unitSettingsApi.list(companyId, { signal: controller.signal });

      if (!aliveRef.current || controller.signal.aborted || unitsAbortRef.current !== controller) return;
      setUnits(rows);
      if (recover) {
        setUnitWriteUncertain(false); setEditingUnit(null);
        setUnitCreate({ ...unitSettingsForm(), unit_name: "", slug: "", address: "", contact_email: "", is_active: true });
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      console.error("Failed to load units", err);
      if (!aliveRef.current || controller.signal.aborted || unitsAbortRef.current !== controller) return;
      setUnits([]);
      setUnitsError(extractApiError(err, "Failed to load units"));
    } finally {
      if (aliveRef.current && unitsAbortRef.current === controller) setUnitsLoading(false);
    }
  };

  const openUnitsModal = async (company) => {
    if (!canUsePage) return;

    setUnitsForCompany(company);
    setUnits([]);
    setUnitsError("");
    setEditingUnit(null);
    setUnitWriteUncertain(false);
    setUnitEdit({ ...unitSettingsForm(), unit_name: "", slug: "", address: "", contact_email: "", is_active: true });
    setUnitCreate({ ...unitSettingsForm(), unit_name: "", slug: "", address: "", contact_email: "", is_active: true });

    await Promise.all([fetchAdminContext(), fetchUnits(company.id)]);
  };

  const closeUnitsModal = () => {
    if (unitSaving) return;
    unitsAbortRef.current?.abort?.();
    setUnitsForCompany(null);
    setUnits([]);
    setUnitsError("");
    setUnitsLoading(false);
    setEditingUnit(null);
  };

  const submitCreateUnit = async (e) => {
    e.preventDefault();
    if (!unitsForCompany || unitSaving || unitsLoading || unitWriteUncertain) return;

    const unit_name = String(unitCreate.unit_name || "").trim();
    if (!unit_name) {
      toast.error("Unit name is required");
      return;
    }

    let settings;
    try { settings = unitSettingsPatch(unitCreate, {}, { creating: true }); }
    catch (error) { toast.error(error.message); return; }
    const payload = {
      ...settings,
      unit_name,
      slug: unitCreate.slug ? toSlugSafe(unitCreate.slug) : undefined,
      address: String(unitCreate.address || "").trim() || null,
      contact_email: String(unitCreate.contact_email || "").trim() || null,
      is_active: unitCreate.is_active !== false,
    };

    setUnitSaving(true);
    const t = toast.loading("Creating unit…");
    try {
      const { units: storedUnits } = await unitSettingsApi.create(unitsForCompany.id, payload);
      if (!aliveRef.current) return;
      setUnits(storedUnits);

      toast.success("Unit created", { id: t });
      setUnitCreate({ ...unitSettingsForm(), unit_name: "", slug: "", address: "", contact_email: "", is_active: true });
    } catch (err) {
      console.error("Create unit error:", err);
      if (err?.uncertain) setUnitWriteUncertain(true);
      const msg = extractApiError(err, "Failed to create unit");
      setUnitsError(msg);
      toast.error(msg, { id: t });
    } finally {
      setUnitSaving(false);
    }
  };

  const startEditUnit = (u) => {
    setEditingUnit(u);
    setUnitsError("");
    setUnitEdit({
      ...unitSettingsForm(u),
      unit_name: u.unit_name || "",
      slug: u.slug || "",
      address: u.address || "",
      contact_email: u.contact_email || "",
      is_active: u.is_active !== false,
    });
  };

  const cancelEditUnit = () => {
    if (unitSaving) return;
    setEditingUnit(null);
  };

  const submitEditUnit = async (e) => {
    e.preventDefault();
    if (!unitsForCompany || !editingUnit || unitSaving || unitsLoading || unitWriteUncertain) return;

    const unit_name = String(unitEdit.unit_name || "").trim();
    if (!unit_name) {
      toast.error("Unit name is required");
      return;
    }

    let settings;
    try { settings = unitSettingsPatch(unitEdit, editingUnit); }
    catch (error) { toast.error(error.message); return; }
    const payload = {
      ...settings,
      unit_name,
      slug: unitEdit.slug ? toSlugSafe(unitEdit.slug) : undefined,
      address: unitEdit.address === "" ? null : String(unitEdit.address),
      contact_email: unitEdit.contact_email === "" ? null : String(unitEdit.contact_email).trim(),
      is_active: unitEdit.is_active !== false,
    };

    setUnitSaving(true);
    const t = toast.loading("Saving unit…");
    try {
      const { units: storedUnits } = await unitSettingsApi.update(unitsForCompany.id, editingUnit.id, payload);
      if (!aliveRef.current) return;
      setUnits(storedUnits);

      toast.success("Unit updated", { id: t });
      setEditingUnit(null);
      await fetchAdminContext();
    } catch (err) {
      console.error("Update unit error:", err);
      if (err?.uncertain) setUnitWriteUncertain(true);
      const msg = extractApiError(err, "Failed to update unit");
      setUnitsError(msg);
      toast.error(msg, { id: t });
    } finally {
      setUnitSaving(false);
    }
  };

  const toggleUnitActive = async (u) => {
    if (!unitsForCompany || unitSaving || unitsLoading || unitWriteUncertain) return;

    setUnitSaving(true);
    const next = !(u.is_active !== false);

    const t = toast.loading(next ? "Activating…" : "Deactivating…");
    try {
      const { units: storedUnits } = await unitSettingsApi.update(unitsForCompany.id, u.id, { is_active: next });
      if (!aliveRef.current) return;
      setUnits(storedUnits);

      toast.success(next ? "Unit activated" : "Unit deactivated", { id: t });
    } catch (err) {
      console.error("Toggle unit active error:", err);
      if (err?.uncertain) setUnitWriteUncertain(true);
      const msg = extractApiError(err, "Failed to update unit");
      setUnitsError(msg);
      toast.error(msg, { id: t });
    } finally {
      setUnitSaving(false);
    }
  };

  const isActiveAdminUnit = (companyId, unitId) => {
    const cId = Number(adminCtx?.company_id);
    const uId = Number(adminCtx?.unit_id);
    return Number(companyId) === cId && Number(unitId) === uId;
  };

  // ---------------- render ----------------
  if (meLoading) {
    return (
      <div className="p-6">
        <p>Loading…</p>
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 space-y-2">
        <h1 className="text-xl font-bold">Companies & Onboarding</h1>
        <p className="text-sm text-red-600">{error || "Forbidden"}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Admin context banner */}
      <div className="border rounded-lg p-3 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm">
            <div className="font-semibold">Platform Admin Context</div>
            {adminCtxLoading ? (
              <div className="text-gray-500 text-xs">Loading context…</div>
            ) : adminCtx ? (
              <div className="text-gray-600 text-xs">
                Company: <b>{adminCtx.company_id}</b> • Unit: <b>{adminCtx.unit_id ?? "—"}</b>
                {adminCtx.unit_slug ? (
                  <>
                    {" "}
                    • Slug: <b>{adminCtx.unit_slug}</b>
                  </>
                ) : null}
              </div>
            ) : (
              <div className="text-gray-500 text-xs">No override set (using your login unit).</div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={fetchAdminContext} disabled={adminCtxLoading}>
              Refresh Context
            </Button>
            <Button variant="outline" onClick={clearAdminContext} disabled={adminCtxLoading || unitSaving}>
              Clear Context
            </Button>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div>
          <h1 className="text-xl font-bold">Companies & Onboarding</h1>
          <p className="text-xs text-gray-500">
            Click a company row to open its admin hub (users, cost centres, units).
          </p>
        </div>
        <Button onClick={() => setShowCompanyForm(true)} disabled={companySaving || companyWriteUncertain}>+ New Company</Button>
      </div>

      {/* Search */}
      <div className="mb-2 flex gap-2 max-w-md">
        <Input placeholder="Search companies..." value={q} onChange={(e) => setQ(e.target.value)} />
        <Button variant="secondary" onClick={() => fetchCompanies({recover:true})} disabled={loading || companySaving}>
          Refresh
        </Button>
      </div>

      {/* Status */}
      {loading && <p>Loading…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {companyWriteUncertain && <p role="alert" className="text-sm text-amber-700">Refresh companies to review the saved state before creating another company.</p>}

      {/* Companies table */}
      {!loading && companies.length === 0 ? (
        <p className="text-sm text-gray-500">No companies found yet.</p>
      ) : (
        <div className="w-full overflow-x-auto">
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Industry</th>
              <th className="border p-2">Location</th>
              <th className="border p-2">Subscription</th>
              <th className="border p-2">Reporting currency</th>
              <th className="border p-2">Created</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr
                key={c.id}
                onClick={() => openCompanyHub(c)}
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                title="Open company hub"
              >
                <td className="border p-2">{c.id}</td>
                <td className="border p-2 font-medium">{c.company_name}</td>
                <td className="border p-2">{c.industry || "—"}</td>
                <td className="border p-2">{c.location || "—"}</td>
                <td className="border p-2">{c.subscription_plan || "—"}</td>
                <td className="border p-2">{c.reporting_currency || "Not configured"}</td>
                <td className="border p-2">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                <td className="border p-2">
                  <div className="flex flex-wrap gap-2">
                    <CompanyCurrencyEditor company={c} onSaved={stored=>setCompanies(current=>current.map(row=>Number(row.id)===Number(stored.id)?{...row,...stored}:row))}/>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openSeedModal(c);
                      }}
                    >
                      Seed Admin
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openUnitsModal(c);
                      }}
                    >
                      Units
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* ───────── New Company Modal ───────── */}
      <Dialog open={showCompanyForm} onClose={() => {if(!companySaving)setShowCompanyForm(false);}}>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="max-h-[90vh] overflow-y-auto bg-white p-6 rounded-xl shadow w-full max-w-md text-gray-900">
            <h2 className="text-lg font-bold mb-4">Create Company</h2>
            <form onSubmit={submitCompanyForm} className="space-y-3">
              <fieldset disabled={companySaving || companyWriteUncertain} className="space-y-3">
              <Input
                placeholder="Company name"
                value={companyForm.company_name}
                onChange={(e) => setCompanyForm((f) => ({ ...f, company_name: e.target.value }))}
                required
              />
              <Input
                placeholder="Industry"
                value={companyForm.industry}
                onChange={(e) => setCompanyForm((f) => ({ ...f, industry: e.target.value }))}
              />
              <Input
                placeholder="Location"
                value={companyForm.location}
                onChange={(e) => setCompanyForm((f) => ({ ...f, location: e.target.value }))}
              />
              <Input
                placeholder="Subscription plan"
                value={companyForm.subscription_plan}
                onChange={(e) => setCompanyForm((f) => ({ ...f, subscription_plan: e.target.value }))}
              />
              <CompanyCurrencyFields value={companyForm} onChange={setCompanyForm}/>
              </fieldset>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCompanyForm(false)} disabled={companySaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={companySaving || companyWriteUncertain}>{companySaving?'Saving…':'Save'}</Button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* ───────── Seed Admin Modal ───────── */}
      <Dialog open={!!seedForCompany} onClose={closeSeedModal}>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow w-full max-w-md text-gray-900">
            <h2 className="text-lg font-bold mb-1">
              Seed Admin for <span className="text-blue-600">{seedForCompany?.company_name}</span>
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              This will create the first admin user for this company and send them an email (if mail is configured).
            </p>
            <form onSubmit={submitSeedAdmin} className="space-y-3">
              <Input
                placeholder="Full name"
                value={seedForm.full_name}
                onChange={(e) => setSeedForm((f) => ({ ...f, full_name: e.target.value }))}
                required
              />
              <Input
                placeholder="Email"
                type="email"
                value={seedForm.email}
                onChange={(e) => setSeedForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <Input
                placeholder="Initial password"
                type="password"
                value={seedForm.password}
                onChange={(e) => setSeedForm((f) => ({ ...f, password: e.target.value }))}
                required
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeSeedModal} disabled={seeding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={seeding}>
                  {seeding ? "Creating…" : "Create Admin"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* ───────── Units Modal ───────── */}
      <Dialog open={!!unitsForCompany} onClose={closeUnitsModal}>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="max-h-[90vh] overflow-y-auto bg-white p-6 rounded-xl shadow w-full max-w-3xl text-gray-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">
                  Units for <span className="text-blue-600">{unitsForCompany?.company_name}</span>{" "}
                  <span className="text-gray-400 font-normal">(#{unitsForCompany?.id})</span>
                </h2>
                <p className="text-xs text-gray-500">Create and manage company units (company_units).</p>
              </div>
              <Button variant="outline" onClick={closeUnitsModal} disabled={unitSaving}>
                Close
              </Button>
            </div>

            <div className="mt-3 border rounded-lg p-3 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-gray-600">
                  Current context:{" "}
                  {adminCtx ? (
                    <>
                      Company <b>{adminCtx.company_id}</b> • Unit <b>{adminCtx.unit_id ?? "—"}</b>
                      {adminCtx.unit_slug ? (
                        <>
                          {" "}
                          • <b>{adminCtx.unit_slug}</b>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-gray-500">none</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={fetchAdminContext} disabled={adminCtxLoading}>
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearAdminContext} disabled={adminCtxLoading || unitSaving}>
                    Clear
                  </Button>
                </div>
              </div>
            </div>

            {unitsError && <p className="mt-3 text-sm text-red-600">{unitsError}</p>}
            {unitWriteUncertain && <p role="alert" className="mt-3 text-sm text-amber-700">Refresh units to review the saved state before making another change.</p>}

            <div className="mt-4 border rounded-lg p-4">
              <h3 className="font-semibold mb-2">Create Unit</h3>
              <form onSubmit={submitCreateUnit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Unit name (required)"
                  value={unitCreate.unit_name}
                  onChange={(e) => setUnitCreate((s) => ({ ...s, unit_name: e.target.value }))}
                  required
                />
                <Input
                  placeholder="Slug (optional)"
                  value={unitCreate.slug}
                  onChange={(e) => setUnitCreate((s) => ({ ...s, slug: e.target.value }))}
                />
                <Input
                  placeholder="Address (optional)"
                  value={unitCreate.address}
                  onChange={(e) => setUnitCreate((s) => ({ ...s, address: e.target.value }))}
                />
                <Input
                  placeholder="Contact email (optional)"
                  type="email"
                  value={unitCreate.contact_email}
                  onChange={(e) => setUnitCreate((s) => ({ ...s, contact_email: e.target.value }))}
                />

                <UnitOperatingSettingsFields value={unitCreate} onChange={setUnitCreate} disabled={unitSaving || unitWriteUncertain} />
                <div className="flex items-center gap-2">
                  <input
                    id="unitCreateActive"
                    type="checkbox"
                    checked={unitCreate.is_active !== false}
                    onChange={(e) => setUnitCreate((s) => ({ ...s, is_active: e.target.checked }))}
                  />
                  <label htmlFor="unitCreateActive" className="text-sm">
                    Active
                  </label>
                </div>

                <div className="md:col-span-2 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => fetchUnits(unitsForCompany?.id, { recover: true })}
                    disabled={unitsLoading || unitSaving}
                  >
                    Refresh
                  </Button>
                  <Button type="submit" disabled={unitSaving || unitsLoading || unitWriteUncertain}>
                    {unitSaving ? "Saving…" : "Create Unit"}
                  </Button>
                </div>
              </form>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Existing Units</h3>
                {unitsLoading && <span className="text-xs text-gray-500">Loading…</span>}
              </div>

              {units.length === 0 && !unitsLoading ? (
                <p className="text-sm text-gray-500 mt-2">No units yet.</p>
              ) : (
                <div className="mt-2 overflow-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2">ID</th>
                        <th className="border p-2">Name</th>
                        <th className="border p-2">Slug</th>
                        <th className="border p-2">Active</th>
                        <th className="border p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((u) => {
                        const active = unitsForCompany?.id && isActiveAdminUnit(unitsForCompany.id, u.id);
                        return (
                          <tr key={u.id} className={active ? "bg-green-50" : ""}>
                            <td className="border p-2">{u.id}</td>
                            <td className="border p-2">{u.unit_name}</td>
                            <td className="border p-2">{u.slug}</td>
                            <td className="border p-2">{u.is_active !== false ? "Yes" : "No"}</td>
                            <td className="border p-2">
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline" onClick={() => startEditUnit(u)} disabled={unitSaving}>
                                  Edit
                                </Button>

                                <Button size="sm" variant="outline" onClick={() => toggleUnitActive(u)} disabled={unitSaving || unitsLoading || unitWriteUncertain}>
                                  {u.is_active !== false ? "Deactivate" : "Activate"}
                                </Button>

                                <Button
                                  size="sm"
                                  variant={active ? "secondary" : "default"}
                                  onClick={() =>
                                    switchAdminContext({
                                      company_id: unitsForCompany.id,
                                      unit_id: u.id,
                                      unit_slug: u.slug,
                                    })
                                  }
                                  disabled={unitSaving || adminCtxLoading || u.is_active === false}
                                  title={u.is_active === false ? "Unit is inactive" : "Switch platform admin context"}
                                >
                                  {active ? "In Context" : "Switch to Unit"}
                                </Button>
                              </div>
                              {active ? (
                                <div className="text-[11px] text-gray-500 mt-1">
                                  This unit is currently selected for platform context.
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {editingUnit && (
              <div className="mt-4 border rounded-lg p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">
                      Edit Unit <span className="text-gray-500 font-normal">#{editingUnit.id}</span>
                    </h3>
                    <p className="text-xs text-gray-500">Update unit details, operating currency, regional settings and default tax.</p>
                  </div>
                  <Button variant="outline" onClick={cancelEditUnit} disabled={unitSaving}>
                    Cancel
                  </Button>
                </div>

                <form onSubmit={submitEditUnit} className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Unit name"
                    value={unitEdit.unit_name}
                    onChange={(e) => setUnitEdit((s) => ({ ...s, unit_name: e.target.value }))}
                    required
                  />
                  <Input
                    placeholder="Slug"
                    value={unitEdit.slug}
                    onChange={(e) => setUnitEdit((s) => ({ ...s, slug: e.target.value }))}
                  />
                  <Input
                    placeholder="Address"
                    value={unitEdit.address}
                    onChange={(e) => setUnitEdit((s) => ({ ...s, address: e.target.value }))}
                  />
                  <Input
                    placeholder="Contact email"
                    type="email"
                    value={unitEdit.contact_email}
                    onChange={(e) => setUnitEdit((s) => ({ ...s, contact_email: e.target.value }))}
                  />

                  <UnitOperatingSettingsFields value={unitEdit} onChange={setUnitEdit} disabled={unitSaving || unitWriteUncertain} />
                  <div className="flex items-center gap-2">
                    <input
                      id="unitEditActive"
                      type="checkbox"
                      checked={unitEdit.is_active !== false}
                      onChange={(e) => setUnitEdit((s) => ({ ...s, is_active: e.target.checked }))}
                    />
                    <label htmlFor="unitEditActive" className="text-sm">
                      Active
                    </label>
                  </div>

                  <div className="md:col-span-2 flex justify-end">
                    <Button type="submit" disabled={unitSaving || unitsLoading || unitWriteUncertain}>
                      {unitSaving ? "Saving…" : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
